import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useCreateUserMutation } from '../../store/reducers/adminApi';

interface CreateUserModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

interface FormData {
  email: string;
  name: string;
  user_role: 'user' | 'psychic';
  enabled: boolean;
  approved: boolean;
  stripe_active: boolean;
  subscription_only: boolean;
  available: boolean;
  bio: string;
  chat_rate: number;
}

const initialFormData: FormData = {
  email: '',
  name: '',
  user_role: 'user',
  enabled: true,
  approved: false,
  stripe_active: false,
  subscription_only: false,
  available: false,
  bio: '',
  chat_rate: 199,
};

export function CreateUserModal({ show, onHide, onSuccess }: CreateUserModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{ tempPassword: string; email: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'chat_rate' ? Number(value) : value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await createUser({
        email: formData.email,
        name: formData.name,
        user_role: formData.user_role,
        enabled: formData.enabled,
        approved: formData.approved,
        stripe_active: formData.stripe_active,
        subscription_only: formData.subscription_only,
        available: formData.available,
        bio: formData.bio || undefined,
        chat_rate: formData.chat_rate,
      }).unwrap();

      setSuccessData({ tempPassword: result.tempPassword, email: formData.email });
      onSuccess?.();
    } catch (err: any) {
      setError(err?.data?.error || 'Failed to create user');
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setError(null);
    setSuccessData(null);
    onHide();
  };

  const isPsychic = formData.user_role === 'psychic';

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New User</Modal.Title>
      </Modal.Header>

      {successData ? (
        <>
          <Modal.Body>
            <Alert variant="success">
              <Alert.Heading>User Created Successfully!</Alert.Heading>
              <p>The user <strong>{successData.email}</strong> has been created.</p>
              <hr />
              <p className="mb-0">
                <strong>Temporary Password:</strong>{' '}
                <code className="user-select-all bg-light p-2 d-inline-block">{successData.tempPassword}</code>
              </p>
              <p className="text-muted mt-2">
                Please share this password securely with the user. They should change it after their first login.
              </p>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="user@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Full Name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>User Type *</Form.Label>
              <Form.Select
                name="user_role"
                value={formData.user_role}
                onChange={handleChange}
              >
                <option value="user">Seeker (Regular User)</option>
                <option value="psychic">Advisor/Reader (Psychic)</option>
              </Form.Select>
            </Form.Group>

            <hr />
            <h6>Status Flags</h6>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enabled"
                label="Enabled (User can log in)"
                checked={formData.enabled}
                onChange={handleChange}
              />
            </Form.Group>

            {isPsychic && (
              <>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="approved"
                    label="Approved (Advisor is approved to take sessions)"
                    checked={formData.approved}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="stripe_active"
                    label="Stripe Active (Payment processing enabled)"
                    checked={formData.stripe_active}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="subscription_only"
                    label="Subscription Only (Only accepts subscription users)"
                    checked={formData.subscription_only}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="available"
                    label="Available (Currently accepting sessions)"
                    checked={formData.available}
                    onChange={handleChange}
                  />
                </Form.Group>

                <hr />
                <h6>Advisor Settings</h6>

                <Form.Group className="mb-3">
                  <Form.Label>Chat Rate (cents/min)</Form.Label>
                  <Form.Control
                    type="number"
                    name="chat_rate"
                    value={formData.chat_rate}
                    onChange={handleChange}
                    min={100}
                    max={1000}
                  />
                  <Form.Text className="text-muted">
                    Rate in cents per minute (e.g., 199 = $1.99/min)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Bio</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Optional biography for the advisor"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}

export default CreateUserModal;
